/**
 * Password Reset Modal
 * 
 * Shows when user clicks password reset link from email.
 * Allows them to set a new password while in recovery session.
 * 
 * Security: Modal is non-dismissible during password recovery.
 * User must either update password or cancel (which signs them out).
 */

import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PasswordResetModal({ isOpen, onClose }: PasswordResetModalProps) {
  const { updatePassword, isPasswordRecovery, signOut } = useAuth();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleClose = async () => {
    // SECURITY: Always sign out when closing without completing password update
    // This prevents leaving a recovery session active
    if (!success && isPasswordRecovery) {
      await signOut();
    }
    
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setLoading(false);
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await updatePassword(newPassword);

      if (updateError) {
        setError(updateError.message);
        setLoading(false);
        return;
      }

      // Success - password updated
      setSuccess(true);
      setLoading(false);
      
      // Sign out after successful password update
      // User will need to sign in with new password
      setTimeout(async () => {
        await signOut();
        handleClose();
      }, 2500);
    } catch (err) {
      setError('An unexpected error occurred');
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      {/* Disable backdrop click during password recovery to prevent accidental dismissal */}
      <Dialog 
        as="div" 
        className="relative z-50" 
        onClose={isPasswordRecovery && !success ? () => {} : handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-dark-card border border-gray-200 dark:border-gray-700 p-8 text-left align-middle shadow-xl transition-all">
                {/* Close button - only show if NOT in recovery mode OR if success */}
                {(!isPasswordRecovery || success) && (
                  <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:text-dark-secondary dark:hover:text-dark-primary transition-colors"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                )}

                {success ? (
                  // Success message
                  <div className="text-center py-8">
                    <div className="mb-4 text-green-600 dark:text-green-400">
                      <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-primary mb-2">
                        Password updated!
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Please sign in with your new password.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-primary mb-2">
                      Set new password
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                      Please enter your new password below.
                    </p>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label
                          htmlFor="new-password"
                          className="block text-sm font-medium text-gray-700 dark:text-dark-secondary mb-2"
                        >
                          New Password
                        </label>
                        <input
                          type="password"
                          id="new-password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-dark-card text-gray-900 dark:text-dark-primary placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-colors"
                          placeholder="••••••••"
                          disabled={loading}
                          autoComplete="new-password"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="confirm-new-password"
                          className="block text-sm font-medium text-gray-700 dark:text-dark-secondary mb-2"
                        >
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          id="confirm-new-password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-dark-card text-gray-900 dark:text-dark-primary placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-colors"
                          placeholder="••••••••"
                          disabled={loading}
                          autoComplete="new-password"
                        />
                      </div>

                      {error && (
                        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                          {error}
                        </div>
                      )}

                      {/* Two buttons: Cancel and Update password */}
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={handleClose}
                          disabled={loading}
                          className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loading}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white font-medium py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? 'Updating...' : 'Update password'}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
